import React from 'react';
import { useLanguage } from '../context/LanguageContext';
import { externalLinks } from '../data/mock';
import { MapPin, Clock, Navigation, ExternalLink } from 'lucide-react';

const Location = () => {
  const { language, t } = useLanguage();

  const locationInfo = [
    { icon: <MapPin size={20} />, text: t('location1') },
    { icon: <Clock size={20} />, text: t('location2') },
    { icon: <Navigation size={20} />, text: t('location3') }
  ];

  // Google Maps embed URLs - Korean labels for KR, English for EN
  const googleMapEmbedKR = 'https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3165.3!2d127.0568!3d37.5113!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x357ca41017ad5d71%3A0x58e4b67507e2bf7a!2z7L2U7JeR7IqkIENPRVg!5e0!3m2!1sko!2skr!4v1700000000000!5m2!1sko!2skr';
  
  const googleMapEmbedEN = 'https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3165.3!2d127.0568!3d37.5113!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x357ca41017ad5d71%3A0x58e4b67507e2bf7a!2sCOEX!5e0!3m2!1sen!2skr!4v1700000000000!5m2!1sen!2skr';

  return (
    <section className="bg-black py-16 md:py-24 animate-fade-in">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="text-center mb-10">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
            {t('locationTitle')}
          </h2>
          
          <div className="flex flex-wrap items-center justify-center gap-6 mb-8">
            {locationInfo.map((info, index) => (
              <div key={index} className="flex items-center gap-2 text-white/80">
                <span className="text-[#00FFD1]">{info.icon}</span>
                <span>{info.text}</span>
              </div>
            ))}
          </div>
          
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            {language === 'ko' ? (
              <>
                <a
                  href={externalLinks.naverMap}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 px-6 py-3 bg-[#00FFD1] text-black font-semibold transition-all duration-300 hover:bg-[#00FFD1]/10 hover:text-[#00FFD1]"
                  style={{ borderRadius: 0 }}
                >
                  {t('viewNaverMap')}
                  <ExternalLink size={16} />
                </a>
                <a
                  href={externalLinks.naverPlace}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 px-6 py-3 bg-white/10 text-white font-semibold transition-all duration-300 hover:bg-white hover:text-black"
                  style={{ borderRadius: 0 }}
                >
                  {t('viewNaverPlace')}
                  <ExternalLink size={16} />
                </a>
              </>
            ) : (
              <>
                <a
                  href="https://maps.google.com/?q=COEX,+Seoul,+South+Korea"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 px-6 py-3 bg-[#00FFD1] text-black font-semibold transition-all duration-300 hover:bg-[#00FFD1]/10 hover:text-[#00FFD1]"
                  style={{ borderRadius: 0 }}
                >
                  View on Google Maps
                  <ExternalLink size={16} />
                </a>
                <a
                  href={externalLinks.naverPlace}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 px-6 py-3 bg-white/10 text-white font-semibold transition-all duration-300 hover:bg-white hover:text-black"
                  style={{ borderRadius: 0 }}
                >
                  {t('viewNaverPlace')}
                  <ExternalLink size={16} />
                </a>
              </>
            )}
          </div>
        </div>
        
        {/* Google Maps Embed - Korean labels for KR, English for EN */}
        <a 
          href={language === 'ko' ? externalLinks.naverMap : 'https://maps.google.com/?q=COEX,+Seoul,+South+Korea'}
          target="_blank"
          rel="noopener noreferrer"
          className="block w-full h-64 md:h-96 bg-white/5 border border-white/10 overflow-hidden relative group"
        >
          <iframe
            src={language === 'ko' ? googleMapEmbedKR : googleMapEmbedEN}
            width="100%"
            height="100%"
            style={{ border: 0, pointerEvents: 'none' }}
            allowFullScreen=""
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
            title={language === 'ko' ? '강남놀이터 코엑스점 위치' : 'Gangnam Playground COEX Location'}
          />
          {/* Overlay for click */}
          <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors flex items-center justify-center">
            <span className="opacity-0 group-hover:opacity-100 transition-opacity bg-[#00FFD1] text-black px-4 py-2 font-semibold flex items-center gap-2">
              {language === 'ko' ? '네이버 지도에서 보기' : 'View on Google Maps'}
              <ExternalLink size={16} />
            </span>
          </div>
        </a>
        
        <p className="text-center text-white/40 text-sm mt-4">
          {language === 'ko' 
            ? '서울시 강남구 삼성동 · 코엑스 도보 5분 · 삼성역 5번 출구' 
            : 'Samseong-dong, Gangnam-gu, Seoul · 5 min walk from COEX · Samseong Stn Exit 5'}
        </p>
      </div>
    </section>
  );
};

export default Location;
